import { useMemo } from 'react'

import { ChainType } from '@lifi/types'

import { useTronWallet } from '@src/hooks/tron/useTronWallet'
import { useAuthContext } from '@src/providers/AuthProvider'
import { EVMAddress, SolanaAddress } from '@src/types/token'
import { WalletConnectionsType } from '@src/types/walletConnection'

export const useWalletConnections = () => {
    const { account, accounts, isEVMConnected, isSolanaConnected } = useAuthContext()

    const { isTronConnected, tronWalletAddress } = useTronWallet()

    const { evmWalletAddress, EVMChainId } = useMemo(() => {
        // First try to use current account if it's EVM
        if (account && account.chainType === ChainType.EVM) {
            return {
                evmWalletAddress: account.address as EVMAddress,
                EVMChainId: account.chainId,
            }
        }

        // Otherwise find the first EVM account in accounts array
        const evmAccount = accounts?.find(acc => acc.chainType === ChainType.EVM)
        return {
            evmWalletAddress: evmAccount?.address as EVMAddress | undefined,
            EVMChainId: evmAccount?.chainId,
        }
    }, [account, accounts])

    const solanaWalletAddress = useMemo(() => {
        // First try to use current account if it's Solana
        if (account && account.chainType === ChainType.SVM) {
            return account.address as SolanaAddress
        }

        // Otherwise find the first Solana account in accounts array
        const solanaAccount = accounts?.find(acc => acc.chainType === ChainType.SVM)
        return solanaAccount?.address as SolanaAddress | undefined
    }, [account, accounts])

    const isAnyWalletConnected = useMemo(
        () => isTronConnected || isSolanaConnected || isEVMConnected,
        [isTronConnected, isSolanaConnected, isEVMConnected]
    )

    return useMemo(
        () =>
            ({
                evmWalletAddress,
                solanaWalletAddress,
                tronWalletAddress,
                isEVMConnected,
                isSolanaConnected,
                isTronConnected,
                isAnyWalletConnected,
                EVMChainId,
            }) as WalletConnectionsType,
        [
            evmWalletAddress,
            solanaWalletAddress,
            tronWalletAddress,
            isEVMConnected,
            isSolanaConnected,
            isTronConnected,
            isAnyWalletConnected,
            EVMChainId,
        ]
    )
}
